import { PropsWithChildren, createContext, useEffect, useRef, useState } from "react";
import { CurrencyCode } from "../types/currency";
import { LoadingSpinner } from "../components/BaseComponents";
import { UserRole } from "../types/UserRole";
import { AccessConfig } from "../types/AccessConfig";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import routes from "../components/Router/routes";
import TrainingNotStarted from "../pages/TrainingNotStarted";
import NoOrganisation from "../pages/NoOrganisation";
import VerifyEmailRequired from "../pages/VerifyEmailRequired";
import { MembershipSummary } from "../pages/SelectOrganisation";
import OrganisationPickerLayout from "../components/OrganisationPickerLayout/OrganisationPickerLayout";
import useAddNotification from "../hooks/useAddNotification";
import { hasNoVisibleOrganisation } from "../lib/sampleState";
import { ASSUMPTION_REFUSED_EVENT, SESSION_EXPIRED_EVENT, ACCOUNT_LOCKED_EVENT, ACCOUNT_LOCK_CODES, ACCESS_AUDIT_UNAVAILABLE_EVENT, READ_ONLY_ACCESS_EVENT } from "../lib/api";

const SESSION_RECHECK_INTERVAL_MS = 60 * 1000;

export type UserData = {
    email: string,
    fullName: string,
    companyId: string,
    companyName: string,
    role: UserRole,
    hasCompletedSetup: boolean,
    currencyCode: CurrencyCode,
    access?: AccessConfig,
    requestedUpgradeAt?: string | null,
    // Server-derived Limited-user rule (no active real membership); never re-derived here.
    canRequestUpgrade?: boolean,
    // B-6b: false until the user proves ownership of their email address.
    emailVerified?: boolean,
    // The caller's own active memberships (B-7 switcher), with fictitiousKind for each company.
    memberships?: MembershipSummary[],
    // Global, UI-only preference: whether demo/training/sample organisations appear in the
    // organisation switcher and sign-in picker. Never gates selecting one server-side.
    showFictitiousOrganisations?: boolean,
    original: {
        companyId: string,
        companyName: string,
    }
}

type UserContextValue = {
    userData?: UserData;
    companyAssumption?: CompanyAssumption|null;
    setCompanyAssumption?: (companyAssumption: CompanyAssumption|null) => void;
    fetchUserData: () => Promise<void>;
    checkAccess: (accessKey: string) => boolean;
    clearUserData: () => void;
}

type CompanyAssumption = {
    type: 'admin'|'delegated',
    companyId: string,
};

const userContext = createContext<UserContextValue>({
    checkAccess: () => false,
    fetchUserData: async () => {},
    clearUserData: () => {},
});

function UserProvider({children}: PropsWithChildren) {
    const [userData, setUserData] = useState<UserData>();
    const [initialLoading, setInitialLoading] = useState<boolean>(true)
    const [error, setError] = useState<any>(null)
    const notify = useAddNotification();
    
    const clearUserData = () => setUserData(undefined);

    const checkAccess = (accessKey: string) => {
        if(!userData) {
            return false;
        }

        const accessStrings: Record<string, boolean> = {
            'user.write': userData.access?.user.permissions.write || false,
            'user.manage': userData.access?.user.permissions.manage || false,
            'company.admin': userData.access?.company.permissions.admin || false,
            'company.portfolios': userData.access?.company.permissions.portfolios || false,
            'company.projects': userData.access?.company.permissions.projects || false,
            'company.invite': userData.access?.company.permissions.invite || false,
            'company.accounting': userData.access?.company.permissions.accounting || false,
            'company.insights': userData.access?.company.permissions.insights || false,
            'company.clientAccounting': userData.access?.company.permissions.clientAccounting || false,
        };

        return accessStrings[accessKey];
    }

    const [companyAssumption, setCompanyAssumption] = useState<CompanyAssumption|null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const assumptionRef = useRef<CompanyAssumption|null>(null);
    assumptionRef.current = companyAssumption;

    // An API call was refused mid-session (e.g. the organisation withdrew admin access): leave the
    // assumed organisation through the normal clear route, which also picks the right dashboard.
    useEffect(() => {
        const onRefused = () => {
            if(!assumptionRef.current) {
                return;
            }
            notify({ body: 'You do not have permission to act as that organisation.' })
            navigate(routes.clearAssumedCompany.path, { replace: true })
        }
        window.addEventListener(ASSUMPTION_REFUSED_EVENT, onRefused)
        return () => window.removeEventListener(ASSUMPTION_REFUSED_EVENT, onRefused)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // D8 (docs/todo/user-org-membership-i-fixes-B.md): a new access session could not be recorded.
    // Distinct from ASSUMPTION_REFUSED_EVENT - the assumption itself is still valid, so this only
    // notifies, it does not clear the assumption or navigate away.
    useEffect(() => {
        const onAuditUnavailable = () => {
            notify({ body: 'Unable to record this access session. Please try again.' })
        }
        window.addEventListener(ACCESS_AUDIT_UNAVAILABLE_EVENT, onAuditUnavailable)
        return () => window.removeEventListener(ACCESS_AUDIT_UNAVAILABLE_EVENT, onAuditUnavailable)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const onReadOnly = () => {
            notify({ body: 'You have view-only access to this organisation, so this change was not saved.' })
        }
        window.addEventListener(READ_ONLY_ACCESS_EVENT, onReadOnly)
        return () => window.removeEventListener(READ_ONLY_ACCESS_EVENT, onReadOnly)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const userDataRef = useRef<UserData>();
    userDataRef.current = userData;
    const lastSessionCheckRef = useRef(Date.now());

    // The server session expires on its own, but a tab left open keeps showing the last screen
    // because nothing asks again: sign out when any call reports the session gone, and re-check
    // when the tab is shown again after a while.
    useEffect(() => {
        const expire = () => {
            if(!userDataRef.current) {
                return;
            }
            setUserData(undefined)
            notify({ body: 'Your session has expired. Please log in again.' })
            navigate(routes.publicLogin.path, { replace: true })
        }
        const lockOut = () => {
            if(!userDataRef.current) {
                return;
            }
            setUserData(undefined)
            navigate(routes.publicLogin.path, { replace: true, state: { accountOnHold: true } })
        }
        const recheck = async () => {
            if(document.visibilityState !== 'visible' || !userDataRef.current) {
                return;
            }
            if(Date.now() - lastSessionCheckRef.current < SESSION_RECHECK_INTERVAL_MS) {
                return;
            }
            lastSessionCheckRef.current = Date.now()
            try {
                const response = await fetch(`${process.env.REACT_APP_BASE_API}/me`, { credentials: 'include' })
                if(response.status === 401 || response.status === 403) {
                    const body = await response.json().catch(() => undefined)
                    if(ACCOUNT_LOCK_CODES.includes(body?.code)) {
                        lockOut()
                    } else {
                        expire()
                    }
                }
            } catch (_) {
                // Offline or a transient failure is not evidence the session ended.
            }
        }
        window.addEventListener(SESSION_EXPIRED_EVENT, expire)
        window.addEventListener(ACCOUNT_LOCKED_EVENT, lockOut)
        document.addEventListener('visibilitychange', recheck)
        window.addEventListener('focus', recheck)
        return () => {
            window.removeEventListener(SESSION_EXPIRED_EVENT, expire)
            window.removeEventListener(ACCOUNT_LOCKED_EVENT, lockOut)
            document.removeEventListener('visibilitychange', recheck)
            window.removeEventListener('focus', recheck)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchUserData = async () => {
        const headers: Record<string, string> = {};
        if(companyAssumption) {
            headers[`neoni-${companyAssumption.type}-assumed-company-id`] = companyAssumption.companyId;
        }
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_API}/me`,
                {
                    headers,
                    credentials: 'include',
                }
            )
            if (!response.ok) {
                // 401/403 simply means the user is not authenticated; treat as
                // logged out rather than an error so public pages stay clean.
                if (companyAssumption && (response.status === 401 || response.status === 403)) {
                    // The organisation (or its permission) refused the assumption. The user is still
                    // signed in as themselves: drop the assumption (which re-fetches /me without the
                    // header) instead of treating them as logged out or retrying the same 403.
                    notify({ body: 'You do not have permission to act as that organisation.' })
                    setCompanyAssumption(null)
                    return
                }
                if (response.status === 401 || response.status === 403) {
                    setUserData(undefined)
                } else {
                    throw new Error('Failed to get user session data')
                }
            } else {
                const body = await response.json()
                setUserData(body.data)
            }
        } catch (error) {
            setError(error)
        } finally {
            setInitialLoading(false)
        }
    }

    useEffect(() => {
        fetchUserData()
		//eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyAssumption])

    if(initialLoading) {
        return <LoadingSpinner />
    }

    if (error) {
        // Handle error here
        console.log('Usercontext error, Navigating to login')
        return <Navigate to={routes.publicLogin.path} />
    }

    // Login refuses unverified accounts (B-6d), so only a session opened before that rule can land
    // here. The backend blocks every route behind Auth for it too; the verify-email and accept-invite
    // pages stay reachable because the emailed link or invitation token is how the account becomes
    // verified.
    if (userData?.emailVerified === false && location.pathname !== routes.acceptInvite.path && location.pathname !== routes.verifyEmail.path) {
        return <VerifyEmailRequired email={userData.email} fullName={userData.fullName} />
    }

    // Decision 11 (docs/todo/admin-user-training-sessions.md): a training user is held on this
    // screen until their session's startDate - every route behind Auth is blocked backend-side
    // too, so this is UX only, not the enforcement itself.
    if (userData?.access?.organisationCategory === 'fictitious' && userData.access.trainingHasStarted === false) {
        return <TrainingNotStarted startDate={userData.access.trainingSessionStartDate} />
    }

    const value = {
        userData,
        companyAssumption,
        setCompanyAssumption,
        fetchUserData,
        checkAccess,
        clearUserData
    };

    // B-7: memberships but none active yet - the user picks one (membership, not assumption) from a
    // reduced dashboard. A single locked organisation lands here too: it is shown as unavailable.
    // Also the case when the user has hidden sample/demo/training organisations and has no real one:
    // there is nothing to show, so the picker's holding page replaces the app even with an active session.
    const memberships = userData?.memberships ?? []
    const hasOnlyHiddenFictitious = hasNoVisibleOrganisation(userData)
    if (userData && (!userData.access || hasOnlyHiddenFictitious) && memberships.length > 0 && location.pathname !== routes.acceptInvite.path && location.pathname !== routes.verifyEmail.path) {
        return (
            <userContext.Provider value={value}>
                <OrganisationPickerLayout />
            </userContext.Provider>
        )
    }

    // Signed in but a member of no organisation: nothing behind Auth can work, so hold here.
    // The public accept-invite and verify-email pages stay reachable so emailed links still work.
    if (userData && !userData.access && userData.memberships?.length === 0 && location.pathname !== routes.acceptInvite.path && location.pathname !== routes.verifyEmail.path) {
        return <NoOrganisation email={userData.email} fullName={userData.fullName} />
    }

    return (
        <userContext.Provider value={value}>{ children }</userContext.Provider>
    )
};

export {
    UserProvider,
    userContext,
};
